import { onBeforeUnmount, onDeactivated, onMounted, reactive } from 'vue';

// Saves an editor's work on its own, so nothing depends on pressing Save: a moment after the
// typing stops, at least every few seconds while it goes on, and straight away when the editor is
// left (another tab in the app, the app going to the background on the iPad, the page closing).
export const AUTOSAVE_DELAY_MS = 1500;
export const AUTOSAVE_MAX_WAIT_MS = 10000;
// A save that failed (no network, the server restarting) is tried again, a little later each time.
export const AUTOSAVE_RETRY_MS = [3000, 10000, 30000];

/**
 * @param {object} options
 * @param {() => Promise<void>} options.save writes the current state
 * @param {() => boolean} options.hasChanges something is not saved yet
 * @param {() => boolean} [options.isEnabled] saving on its own is allowed now
 * @param {(error: unknown) => boolean} [options.isConflict] the save was refused because the
 *   document changed elsewhere; saving waits until the editor settles it with reset()
 * @param {(error: unknown) => boolean} [options.isFatal] trying the same save again cannot help
 * @returns {{ state: { status: 'idle'|'pending'|'saving'|'saved'|'error'|'conflict', savedAt: Date|null, error: unknown },
 *   schedule: () => void, flush: (options?: { force?: boolean }) => Promise<unknown>,
 *   saveNow: () => Promise<void>, reset: (status?: string) => void }}
 */
export function useAutosave({
	save,
	hasChanges,
	isEnabled = () => true,
	isConflict = () => false,
	isFatal = () => false,
	delay = AUTOSAVE_DELAY_MS,
	maxWait = AUTOSAVE_MAX_WAIT_MS,
} = {}) {
	const state = reactive({ status: 'idle', savedAt: null, error: null });
	let debounceTimer = null;
	let maxWaitTimer = null;
	let retryTimer = null;
	let retries = 0;
	let running = null;
	let requested = null;
	let disposed = false;

	const clearTimers = () => {
		clearTimeout(debounceTimer);
		clearTimeout(maxWaitTimer);
		clearTimeout(retryTimer);
		debounceTimer = null;
		maxWaitTimer = null;
		retryTimer = null;
	};

	const saveOnce = async ({ force }) => {
		// A refused save waits for the editor to decide; it is never overwritten on its own.
		if (state.status === 'conflict') return state.error;
		if (!isEnabled() || (!force && !hasChanges())) {
			if (state.status === 'pending') state.status = state.savedAt ? 'saved' : 'idle';
			return null;
		}
		state.status = 'saving';
		try {
			await save();
			retries = 0;
			state.error = null;
			state.savedAt = new Date();
			state.status = 'saved';
			return null;
		} catch (error) {
			state.error = error;
			if (isConflict(error)) {
				state.status = 'conflict';
				return error;
			}
			state.status = 'error';
			if (!disposed && !isFatal(error)) {
				retryTimer = setTimeout(() => flush(), AUTOSAVE_RETRY_MS[Math.min(retries, AUTOSAVE_RETRY_MS.length - 1)]);
				retries += 1;
			}
			return error;
		}
	};

	/** Saves what is not saved yet. Resolves with the error of the last save, or null. */
	const flush = ({ force = false } = {}) => {
		clearTimers();
		if (running) {
			// One save at a time: this one follows the save already on its way.
			requested = { force: force || Boolean(requested?.force) };
			return running;
		}
		running = (async () => {
			let request = { force };
			let lastError = null;
			while (request) {
				requested = null;
				lastError = await saveOnce(request);
				request = requested;
			}
			return lastError;
		})().finally(() => {
			running = null;
			// Typing that went on while the save was on its way is saved once it stops.
			if (!disposed && state.status === 'saved' && hasChanges()) schedule();
		});
		return running;
	};

	const schedule = () => {
		if (disposed || state.status === 'conflict' || !isEnabled()) return;
		if (state.status !== 'saving' && state.status !== 'error') state.status = 'pending';
		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => flush(), delay);
		if (!maxWaitTimer) maxWaitTimer = setTimeout(() => flush(), maxWait);
	};

	/** Saves straight away, even with nothing new, and throws when the save fails. */
	const saveNow = async () => {
		const error = await flush({ force: true });
		if (error) throw error;
	};

	/** Starts again after the document was reloaded or a conflict was settled. */
	const reset = (status = 'idle') => {
		clearTimers();
		retries = 0;
		state.status = status;
		state.error = null;
	};

	const onVisibilityChange = () => {
		if (document.visibilityState === 'hidden') void flush();
	};
	const onPageHide = () => {
		void flush();
	};
	const onOnline = () => {
		if (state.status === 'error') void flush();
	};
	// Closing or reloading the page with work not saved yet asks first, and saves meanwhile.
	const onBeforeUnload = (event) => {
		if (!hasChanges() && !running) return;
		void flush();
		event.preventDefault();
		event.returnValue = '';
	};

	onMounted(() => {
		document.addEventListener('visibilitychange', onVisibilityChange);
		window.addEventListener('pagehide', onPageHide);
		window.addEventListener('online', onOnline);
		window.addEventListener('beforeunload', onBeforeUnload);
	});

	// Leaving the editor's tab in the app saves at once.
	onDeactivated(() => {
		void flush();
	});

	// Closing the editor's tab saves what is left; the save finishes after the editor is gone.
	onBeforeUnmount(() => {
		void flush();
		disposed = true;
		document.removeEventListener('visibilitychange', onVisibilityChange);
		window.removeEventListener('pagehide', onPageHide);
		window.removeEventListener('online', onOnline);
		window.removeEventListener('beforeunload', onBeforeUnload);
	});

	return { state, schedule, flush, saveNow, reset };
}
